import { useEffect } from "react";

/**
 * Silent self-heal for Service-Worker-cached PWAs.
 *
 * Flow when a new build is deployed:
 *   1. Browser visits paperkraftcards.in → fetches /sw.js (now ALWAYS network,
 *      thanks to `updateViaCache: 'none'` in index.js).
 *   2. New SW file differs from cached one → install event runs (`self.skipWaiting()`)
 *   3. New SW activates → wipes old shell caches → calls `clients.claim()`
 *   4. `navigator.serviceWorker.controller` swaps → fires `controllerchange`
 *   5. We hear that event here and reload the page exactly once per session
 *      → fresh JS bundle + fresh API responses, no "Failed to load catalog" toast.
 *
 * Triggers for the update check:
 *   - 5-minute polling (long-lived tabs)
 *   - **visibilitychange → visible**: app returns to foreground / PWA reopens
 *     from app-switcher → immediate update check. This is the big one: it
 *     catches every installed PWA the first time the customer interacts with
 *     it after a deploy, instead of waiting up to 5 min.
 *   - **focus**: covers desktop tabs and mobile browsers that don't fire
 *     visibilitychange reliably (older Samsung Internet, in-app webviews).
 *
 * The sessionStorage guard prevents the reload loop edge-case if multiple
 * SWs race to claim during the same load.
 */
export function useServiceWorkerAutoUpdate() {
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;

    const onControllerChange = () => {
      if (sessionStorage.getItem("sv_sw_reloaded") === "1") return;
      sessionStorage.setItem("sv_sw_reloaded", "1");
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    let inflight = false;
    const checkForUpdate = async () => {
      // Single-flight guard — focus + visibilitychange both fire on the same
      // resume in some browsers; we only need one fetch per event burst.
      if (inflight) return;
      inflight = true;
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.update()));
      } catch {
        /* network blip — try again next tick */
      } finally {
        // Tiny cool-down so two firings within ~1.5 s coalesce.
        window.setTimeout(() => { inflight = false; }, 1500);
      }
    };

    // Periodic update check for long-running tabs.
    const pollId = setInterval(checkForUpdate, 5 * 60 * 1000);

    // App-resume update check — runs the SECOND a customer opens their
    // installed PWA from the home-screen icon or switches back from another
    // app. Combined with `updateViaCache: 'none'`, this means: new SW
    // detected → claim → controllerchange → silent reload, all in <1 s.
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") checkForUpdate();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", checkForUpdate);

    // Also fire one explicit check on mount — covers the case where the SW
    // had been sleeping while the page was in bfcache.
    checkForUpdate();

    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", checkForUpdate);
      clearInterval(pollId);
    };
  }, []);
}
